import { SettingsBase } from 'src/api';
import { mapRecord, GridColumn, GridColumnV2, arrayToMap } from '@schenck/rotec-components';
import { Omit } from 'lodash';
import { ColumnTitle } from 'src/services';

/**
 * Convenience config to reuse common things accross settings and grid columns.
 */
export type ColumnBaseConfig<T> = { key: keyof T; width: string; name: string; invisible?: true }[];

/**
 * Grid Column definition without width.
 */
export type PartialColumn = Omit<GridColumnV2, 'width'>;

/**
 * Create a settings object from the order of columns and the base config object.
 */
export function columnBaseToSettings<T>(config: ColumnBaseConfig<T>): SettingsBase<keyof T> {
  return {
    columnOrder: config.map((entry) => entry.key),
    displayedColumns: config.filter((entry) => !entry.invisible).map((entry) => entry.key),
    columnsWidths: <Partial<Record<keyof T, string>>>(
      mapRecord(arrayToMap(config, 'key'), (entry) => entry.width)
    ),
    pageSize: 50,
  };
}

/**
 * Adds missing properties to the columns to make it full fledged GridColumns.
 */
export function addTitleAndWidthToGridColumn<T>(
  columns: PartialColumn[],
  config: ColumnBaseConfig<T>
): Partial<Record<keyof T, GridColumn>> {
  const fullColumns = columns.map((column) => {
    const configEntry = config.find((entry) => entry.key === column.key);
    if (!configEntry) {
      throw new Error(`Key "${column.key}" is missing in ColumnBaseConfig`);
    }
    return <GridColumn>{
      ...column,
      width: configEntry.width,
      title: column.title === undefined ? configEntry.name : column.title,
      invisible: configEntry.invisible,
    };
  });
  return <Partial<Record<keyof T, GridColumn>>>arrayToMap(fullColumns, 'key');
}

export function configToSettingsColumnTitles<T>(
  columns: ColumnBaseConfig<T>
): ColumnTitle<keyof T>[] {
  return columns.map((column) => ({ key: column.key, name: column.name }));
}
